import React, { createContext, useContext, useEffect, useMemo, useReducer, useState } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "smiling-roots-cart-v2";

const initial = { items: [] };

function reducer(state, action) {
  switch (action.type) {
    case "hydrate":
      return action.payload || initial;
    case "add": {
      const existing = state.items.find((i) => i.id === action.item.id);
      const items = existing
        ? state.items.map((i) =>
            i.id === action.item.id
              ? { ...i, quantity: i.quantity + (action.qty || 1) }
              : i
          )
        : [...state.items, { ...action.item, quantity: action.qty || 1 }];
      return { ...state, items };
    }
    case "remove":
      return { ...state, items: state.items.filter((i) => i.id !== action.id) };
    case "setQty":
      return {
        ...state,
        items: state.items
          .map((i) => (i.id === action.id ? { ...i, quantity: action.qty } : i))
          .filter((i) => i.quantity > 0),
      };
    case "clear":
      return { ...state, items: [] };
    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initial);
  const [open, setOpen] = useState(false);
  const [discount, setDiscount] = useState(null); // { code, percent_off }
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) dispatch({ type: "hydrate", payload: JSON.parse(raw) });
    } catch (e) {
      // ignore
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      // ignore
    }
  }, [state, hydrated]);

  const subtotal = useMemo(
    () => state.items.reduce((s, i) => s + i.price * i.quantity, 0),
    [state.items]
  );
  const discountAmt = useMemo(
    () => (discount ? subtotal * (discount.percent_off / 100) : 0),
    [subtotal, discount]
  );
  const total = Math.max(0, subtotal - discountAmt);
  const count = state.items.reduce((s, i) => s + i.quantity, 0);

  const value = {
    items: state.items,
    open,
    setOpen,
    add: (item, qty = 1) => {
      dispatch({ type: "add", item, qty });
      setOpen(true);
    },
    remove: (id) => dispatch({ type: "remove", id }),
    setQty: (id, qty) => dispatch({ type: "setQty", id, qty }),
    clear: () => dispatch({ type: "clear" }),
    subtotal,
    total,
    count,
    discount,
    setDiscount,
    discountAmt,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
